import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Tarteaucitron.js consent banner loader.
 *
 * Why Tarteaucitron?
 *   - 100% free and open-source (MIT), hosted on GitHub, no account needed.
 *   - Native Google Consent Mode v2 compatibility.
 *   - Trilingual (FR, NL, EN) language packs built in.
 *
 * Why a React component?
 *   - We load scripts dynamically so we can pick the right language pack at runtime
 *     based on the current i18n language.
 *   - We register two custom services ("google analytics" consent + "google ads"
 *     consent) that bridge user choices to `gtag('consent', 'update', ...)`.
 *     When the site owner later adds GA4 or Meta Pixel, those trackers will
 *     automatically respect the Consent Mode v2 state — no code change needed.
 *
 * No API keys, no tracking, no external account required.
 */
const TAC_VERSION = '1.32.0';
const TAC_BASE = `https://cdn.jsdelivr.net/gh/AmauriC/tarteaucitron.js@${TAC_VERSION}`;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = false; // preserve ordering: core -> lang -> init
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function tacLangFor(i18nLang) {
  const l = (i18nLang || 'fr').slice(0, 2).toLowerCase();
  return l === 'nl' || l === 'en' ? l : 'fr';
}

/** Declarative initialization — idempotent. Runs ONCE after scripts load. */
function initTarteaucitron(legalUrl) {
  if (window.__tac_initialized) return;
  if (!window.tarteaucitron) return;

  window.tarteaucitron.init({
    privacyUrl: legalUrl,
    bodyPosition: 'bottom',           // show banner above content
    hashtag: '#tarteaucitron',
    cookieName: 'flexi_tac',
    orientation: 'bottom',            // bottom banner
    groupServices: true,               // group by category
    showDetailsOnClick: true,
    serviceDefaultState: 'wait',       // nothing accepted by default (RGPD)
    showAlertSmall: false,             // hide the small reopener (we use the Footer link instead)
    cookieslist: true,                 // show the cookie list in UI
    closePopup: false,
    showIcon: false,                   // hide the floating cookie icon (we use the Footer link instead)
    iconPosition: 'BottomRight',
    adblocker: false,
    DenyAllCta: true,
    AcceptAllCta: true,
    highPrivacy: true,                 // nothing runs until user consents
    handleBrowserDNTRequest: false,
    removeCredit: false,
    moreInfoLink: true,
    useExternalCss: false,
    useExternalJs: false,
    mandatory: true,
    mandatoryCta: true,
  });

  // --- Custom service 1: Analytics (Google Consent Mode v2 bridge) ---
  window.tarteaucitron.services.gcmAnalytics = {
    key: 'gcmAnalytics',
    type: 'analytic',
    name: 'Mesure d\'audience (Google Analytics)',
    uri: 'https://policies.google.com/privacy',
    needConsent: true,
    cookies: ['_ga', '_gid', '_gat', '_gac_'],
    js: function () {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    },
    fallback: function () {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    },
  };

  // --- Custom service 2: Ads (Meta Pixel + Google Ads) ---
  window.tarteaucitron.services.gcmAds = {
    key: 'gcmAds',
    type: 'ads',
    name: 'Publicités personnalisées (Google Ads, Meta Pixel)',
    uri: 'https://policies.google.com/privacy',
    needConsent: true,
    cookies: ['_fbp', 'fr', '_gcl_au', 'IDE'],
    js: function () {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
    },
    fallback: function () {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    },
  };

  (window.tarteaucitron.job = window.tarteaucitron.job || []).push('gcmAnalytics');
  window.tarteaucitron.job.push('gcmAds');

  window.__tac_initialized = true;
}

export default function TarteauCitronLoader() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = tacLangFor(i18n.language);
    // Map language to the legal "Privacy policy" URL shown inside the banner
    const legalUrl = '/confidentialite';

    (async () => {
      try {
        // 1. Core script
        await loadScript(`${TAC_BASE}/tarteaucitron.min.js`);
        // 2. Language pack (must come after core)
        await loadScript(`${TAC_BASE}/lang/tarteaucitron.${lang}.js`);
        // 3. Init (idempotent) — pushes our services + renders UI on first call
        initTarteaucitron(legalUrl);
        // 4. If language changed after init, refresh language strings
        if (window.tarteaucitron && window.tarteaucitronForceLanguage && window.tarteaucitron.lang) {
          window.tarteaucitron.lang = window[`tarteaucitronForceLanguage`] || window.tarteaucitron.lang;
        }
      } catch (e) {
        // CDN unreachable or blocked — silent fallback, no banner shown.
        // eslint-disable-next-line no-console
        console.warn('[TarteauCitron] load failed', e);
      }
    })();
  }, [i18n.language]);

  return null;
}

/** Re-open the consent panel (hook this to "Manage my cookies" links). */
export function openCookiesPanel() {
  if (typeof window === 'undefined') return;
  try {
    if (window.tarteaucitron && typeof window.tarteaucitron.userInterface?.openPanel === 'function') {
      window.tarteaucitron.userInterface.openPanel();
    } else if (window.tarteaucitron) {
      // Fallback: legacy API
      window.location.hash = '#tarteaucitron';
    }
  } catch {
    /* ignore */
  }
}
